import pick from 'lodash/pick';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import SearchComponent from '../../Table/SearchComponent';
import RecipientsComponent from './RecipientsComponent';

import { checkHasPermissions, getSelectValue } from '../../../utils';

import permissions from '../../../permissions';
import { ActionButton } from '../../Button';
import StyledSelect from '../../Select';
import { MessageRecipientSkeleton } from '../../Shared/skeletons';
import { StyledTab, StyledTabs } from '../../Tabs/StyledTabs';

const CreateMessageComponent = (props) => {
  const { t } = useTransl();
  const {
    buildingId,
    selectedValue,
    selectOptions,
    onSelectChangeAndRefresh,
    isGroups,
    onPersonsOrGroupsChecked,
    onSearchChange,
    search,
    showLoader,
    showNotFound,
    hasSelectedRecipients,
    hasTwoOrMoreBuildings,
    handleNewChat,
  } = props;

  const height = 300;

  const recipientsProps = pick(props, [
    'nextRecipients',
    'loadNextRecipients',
    'recipients',
    'groups',
    'groupKeys',
    'isGroups',
    'onAddRecipient',
    'onAddGroup',
    'checkedRecipients',
    'checkedGroups',
    'isLoading',
    'roles',
    'buildings',
    'insertBuildingName',
  ]);

  return (
    <>
      <Box mt="2rem">
        {hasTwoOrMoreBuildings && !buildingId && (
          <StyledSelect
            value={getSelectValue(selectedValue, selectOptions)}
            onChange={onSelectChangeAndRefresh}
            options={selectOptions}
            sortAlphabetically={false}
          />
        )}
        <Box my="1.5rem">
          <StyledTabs
            value={isGroups ? 'groups' : 'persons'}
            onChange={(event, newValue) => onPersonsOrGroupsChecked(newValue)}
            indicatorColor="primary"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ '& button': { maxWidth: '49%', width: '49%' } }}
          >
            <StyledTab label={t('messages.people')} value="persons" />
            <StyledTab
              label={t('messages.groups')}
              value="groups"
              disabled={!checkHasPermissions(permissions.send_message_to_group)}
            />
          </StyledTabs>
        </Box>

        <SearchComponent
          placeholder={t('title.search')}
          onChange={onSearchChange}
          value={search}
          fullWidth
        />

        <Box my="2rem" height={height}>
          {showLoader ? (
            Array.from({ length: 5 }).map((v, i) => (
              <MessageRecipientSkeleton key={i} />
            ))
          ) : showNotFound ? (
            <div>{t('messages.noRecipientsFound')}</div>
          ) : (
            <RecipientsComponent height={height} {...recipientsProps} />
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" width="100%" pb="1.5rem">
        <ActionButton onClick={handleNewChat} disabled={!hasSelectedRecipients}>
          {t('messages.next')}
        </ActionButton>
      </Box>
    </>
  );
};

CreateMessageComponent.defaultProps = {
  buildingId: null,
  search: '',
  selectedValue: null,
  selectOptions: [],
};

CreateMessageComponent.propTypes = {
  buildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedValue: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
  onSelectChangeAndRefresh: PropTypes.func.isRequired,
  isGroups: PropTypes.bool.isRequired,
  onPersonsOrGroupsChecked: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  search: PropTypes.string,
  showLoader: PropTypes.bool.isRequired,
  showNotFound: PropTypes.bool.isRequired,
  hasSelectedRecipients: PropTypes.bool.isRequired,
  hasTwoOrMoreBuildings: PropTypes.bool.isRequired,
  handleNewChat: PropTypes.func.isRequired,
};

export default CreateMessageComponent;
